<template>
  <div class="historical-comparison-container">
    <div class="comparison-header">
      <div class="header-left">
        <h6 class="comparison-title">
          <i class="ri-line-chart-line me-2"></i>
          Comparativas Históricas
        </h6>
        <p class="comparison-subtitle">Analiza tendencias y evolución de datos</p>
      </div>
      <div class="header-controls">
        <el-select 
          v-model="selectedMetric" 
          placeholder="Seleccionar métrica"
          size="small"
          @change="loadHistoricalData"
        >
          <el-option 
            v-for="metric in availableMetrics"
            :key="metric.value"
            :label="metric.label"
            :value="metric.value"
          >
            <i :class="metric.icon" class="me-2"></i>
            {{ metric.label }}
          </el-option>
        </el-select>
        
        <el-select 
          v-model="selectedPeriod" 
          placeholder="Período"
          size="small"
          @change="loadHistoricalData"
        >
          <el-option label="Últimos 7 días" value="7d"></el-option>
          <el-option label="Últimos 30 días" value="30d"></el-option>
          <el-option label="Últimos 3 meses" value="3m"></el-option>
          <el-option label="Últimos 6 meses" value="6m"></el-option>
          <el-option label="Último año" value="1y"></el-option>
        </el-select>
        
        <el-button-group size="small">
          <el-tooltip content="Configurar comparación" placement="top">
            <el-button @click="showConfigModal = true">
              <i class="ri-settings-line"></i>
            </el-button>
          </el-tooltip>
          <el-tooltip content="Exportar comparación" placement="top">
            <el-button @click="exportComparison">
              <i class="ri-download-line"></i>
            </el-button>
          </el-tooltip>
        </el-button-group>
      </div>
    </div>
    
    <div class="comparison-content">
      <!-- Indicadores de cambio -->
      <div class="change-indicators">
        <div 
          v-for="indicator in changeIndicators"
          :key="indicator.label"
          class="change-indicator"
        >
          <div class="indicator-icon" :class="indicator.trend">
            <i :class="indicator.icon"></i>
          </div>
          <div class="indicator-content">
            <span class="indicator-label">{{ indicator.label }}</span>
            <div class="indicator-values">
              <span class="current-value">{{ indicator.currentValue }}</span>
              <span class="change-value" :class="indicator.trend">
                {{ indicator.changeValue }}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Gráfico de tendencias -->
      <div class="trends-chart-container">
        <InteractiveChart
          :title="`Tendencia: ${selectedMetricLabel}`"
          :subtitle="`Período: ${selectedPeriodLabel}`"
          chart-type="line"
          :data="trendsChartData"
          :options="trendsChartOptions"
          :interactive="true"
          @chart-click="onTrendClick"
        />
      </div>
      
      <!-- Tabla de comparación -->
      <div class="comparison-table-container">
        <div class="table-header">
          <h6>Comparación Detallada</h6>
          <el-button size="small" @click="toggleTableView">
            <i :class="tableViewIcon" class="me-1"></i>
            {{ tableViewLabel }}
          </el-button>
        </div>
        
        <el-table
          :data="comparisonTableData"
          stripe
          style="width: 100%"
          :default-sort="{ prop: 'period', order: 'descending' }"
          v-loading="isLoading"
        >
          <el-table-column prop="period" label="Período" sortable width="120">
            <template #default="scope">
              <span class="period-cell">{{ scope.row.period }}</span>
            </template>
          </el-table-column>
          
          <el-table-column 
            v-for="metric in visibleMetrics"
            :key="metric.value"
            :prop="metric.value"
            :label="metric.label"
            sortable
            align="right"
          >
            <template #default="scope">
              <div class="metric-cell">
                <span class="metric-value">{{ formatValue(scope.row[metric.value], metric.format) }}</span>
                <span 
                  v-if="scope.row[`${metric.value}_change`]"
                  class="metric-change"
                  :class="getChangeClass(scope.row[`${metric.value}_change`])"
                >
                  {{ formatChange(scope.row[`${metric.value}_change`]) }}
                </span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    
    <!-- Modal de configuración -->
    <el-dialog
      v-model="showConfigModal"
      title="Configurar Comparación"
      width="500px"
    >
      <el-form :model="comparisonConfig" label-width="150px">
        <el-form-item label="Métricas a mostrar">
          <el-checkbox-group v-model="comparisonConfig.selectedMetrics">
            <el-checkbox 
              v-for="metric in availableMetrics"
              :key="metric.value"
              :label="metric.value"
            >
              {{ metric.label }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        
        <el-form-item label="Tipo de agregación">
          <el-radio-group v-model="comparisonConfig.aggregation">
            <el-radio label="daily">Diario</el-radio>
            <el-radio label="weekly">Semanal</el-radio>
            <el-radio label="monthly">Mensual</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item label="Mostrar cambios">
          <el-switch v-model="comparisonConfig.showChanges"></el-switch>
        </el-form-item>
        
        <el-form-item label="Incluir predicciones">
          <el-switch v-model="comparisonConfig.includePredictions"></el-switch>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showConfigModal = false">Cancelar</el-button>
          <el-button type="primary" @click="applyConfiguration">Aplicar</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, reactive, onMounted, watch } from 'vue'
import { ElMessage } from 'element-plus'
import InteractiveChart from './InteractiveChart.vue'
import * as XLSX from 'xlsx'

export default {
  name: 'HistoricalComparison',
  components: {
    InteractiveChart
  },
  props: {
    dataSource: {
      type: String,
      default: 'dashboard'
    }
  },
  emits: ['data-request'],
  setup() {
    const selectedMetric = ref('projects')
    const selectedPeriod = ref('30d')
    const isLoading = ref(false)
    const showConfigModal = ref(false)
    const isTableExpanded = ref(false)
    
    // Métricas disponibles
    const availableMetrics = [
      { value: 'projects', label: 'Proyectos', icon: 'ri-funds-line', format: 'number' },
      { value: 'subprojects', label: 'Subproyectos', icon: 'ri-building-line', format: 'number' },
      { value: 'works', label: 'Obras', icon: 'ri-hammer-line', format: 'number' },
      { value: 'users', label: 'Usuarios Activos', icon: 'ri-user-line', format: 'number' },
      { value: 'activity', label: 'Actividad', icon: 'ri-pulse-line', format: 'number' }
    ]
    
    // Configuración de comparación
    const comparisonConfig = reactive({
      selectedMetrics: ['projects', 'works'],
      aggregation: 'daily',
      showChanges: true,
      includePredictions: false
    })
    
    // Datos simulados para demostración
    const historicalData = ref({})
    
    // Computeds
    const selectedMetricLabel = computed(() => {
      const metric = availableMetrics.find(m => m.value === selectedMetric.value)
      return metric ? metric.label : ''
    })
    
    const selectedPeriodLabel = computed(() => {
      const periods = {
        '7d': 'Últimos 7 días',
        '30d': 'Últimos 30 días',
        '3m': 'Últimos 3 meses',
        '6m': 'Últimos 6 meses',
        '1y': 'Último año'
      }
      return periods[selectedPeriod.value] || ''
    })
    
    const visibleMetrics = computed(() => {
      return availableMetrics.filter(m => 
        comparisonConfig.selectedMetrics.includes(m.value)
      )
    })
    
    const tableViewIcon = computed(() => {
      return isTableExpanded.value ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
    })
    
    const tableViewLabel = computed(() => {
      return isTableExpanded.value ? 'Contraer' : 'Expandir'
    })
    
    // Indicadores de cambio
    const changeIndicators = computed(() => {
      const currentData = historicalData.value.current || {}
      const previousData = historicalData.value.previous || {}
      
      return visibleMetrics.value.map(metric => {
        const current = currentData[metric.value] || 0
        const previous = previousData[metric.value] || 0
        const change = current - previous
        const percentChange = previous !== 0 ? ((change / previous) * 100) : 0
        
        return {
          label: metric.label,
          currentValue: formatValue(current, metric.format),
          changeValue: formatChange(percentChange),
          trend: change >= 0 ? 'positive' : 'negative',
          icon: change >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'
        }
      })
    })
    
    // Datos del gráfico de tendencias
    const trendsChartData = computed(() => {
      const trends = historicalData.value.trends || []
      
      return {
        labels: trends.map(t => t.date),
        datasets: [{
          label: selectedMetricLabel.value,
          data: trends.map(t => t[selectedMetric.value] || 0),
          borderColor: '#4361ee',
          backgroundColor: 'rgba(67, 97, 238, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#4361ee',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8
        }]
      }
    })
    
    // Opciones del gráfico de tendencias
    const trendsChartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(20, 20, 20, 0.9)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y}`
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            maxRotation: 45
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        }
      }
    }))
    
    // Datos de la tabla de comparación
    const comparisonTableData = computed(() => {
      const trends = historicalData.value.trends || []
      
      return trends.map((trend, index) => {
        const row = {
          period: trend.date,
          ...trend
        }
        
        // Calcular cambios respecto al período anterior
        if (index > 0) {
          const previous = trends[index - 1]
          visibleMetrics.value.forEach(metric => {
            const current = trend[metric.value] || 0
            const prev = previous[metric.value] || 0
            const change = prev !== 0 ? ((current - prev) / prev) * 100 : 0
            row[`${metric.value}_change`] = change
          })
        }
        
        return row
      }).reverse() // Mostrar más recientes primero
    })
    
    // Métodos
    const loadHistoricalData = async () => {
      isLoading.value = true
      
      try {
        // Simular carga de datos históricos
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Generar datos simulados
        const days = selectedPeriod.value === '7d' ? 7 : 
                     selectedPeriod.value === '30d' ? 30 :
                     selectedPeriod.value === '3m' ? 90 :
                     selectedPeriod.value === '6m' ? 180 : 365
        
        const trends = generateTrendsData(days)
        
        historicalData.value = {
          current: trends[trends.length - 1] || {},
          previous: trends[trends.length - 2] || {},
          trends
        }
        
        ElMessage.success('Datos históricos cargados')
      } catch (error) {
        // console.error('Error loading historical data:', error)
        ElMessage.error('Error al cargar datos históricos')
      } finally {
        isLoading.value = false
      }
    }
    
    const generateTrendsData = (days) => {
      const data = []
      const today = new Date()
      
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(today)
        date.setDate(date.getDate() - i)
        
        const trend = Math.sin(i * 0.1) * 50 + Math.random() * 20
        
        data.push({
          date: date.toLocaleDateString('es-ES', { 
            day: '2-digit', 
            month: '2-digit' 
          }),
          projects: Math.max(0, Math.floor(10 + trend)),
          subprojects: Math.max(0, Math.floor(5 + trend * 0.5)),
          works: Math.max(0, Math.floor(50 + trend * 2)),
          users: Math.max(0, Math.floor(20 + trend * 0.8)),
          activity: Math.max(0, Math.floor(100 + trend * 3))
        })
      }
      
      return data
    }
    
    const formatValue = (value, format) => {
      if (typeof value !== 'number') return '-'
      
      switch (format) {
        case 'currency':
          return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
          }).format(value)
        case 'percentage':
          return `${value.toFixed(1)}%`
        default:
          return value.toLocaleString('es-ES')
      }
    }
    
    const formatChange = (change) => {
      if (typeof change !== 'number') return ''
      const sign = change >= 0 ? '+' : ''
      return `${sign}${change.toFixed(1)}%`
    }
    
    const getChangeClass = (change) => {
      if (change > 0) return 'positive'
      if (change < 0) return 'negative'
      return 'neutral'
    }
    
    const onTrendClick = (data) => {
      // console.log('Trend clicked:', data)
      // Implementar lógica adicional para el click en tendencias
      ElMessage.info(`Tendencia seleccionada: ${data.label}`)
    }
    
    const toggleTableView = () => {
      isTableExpanded.value = !isTableExpanded.value
    }
    
    const applyConfiguration = () => {
      showConfigModal.value = false
      loadHistoricalData()
      ElMessage.success('Configuración aplicada')
    }
    
    const exportComparison = async () => {
      try {
        const workbook = XLSX.utils.book_new()
        
        // Hoja de datos de comparación
        const worksheetData = [
          ['Período', ...visibleMetrics.value.map(m => m.label)],
          ...comparisonTableData.value.map(row => [
            row.period,
            ...visibleMetrics.value.map(m => row[m.value] || 0)
          ])
        ]
        
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Comparación Histórica')
        
        const fileName = `comparacion_historica_${selectedMetric.value}_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(workbook, fileName)
        
        ElMessage.success('Comparación exportada correctamente')
      } catch (error) {
        // console.error('Error exporting comparison:', error)
        ElMessage.error('Error al exportar comparación')
      }
    }
    
    // Watchers
    watch([selectedMetric, selectedPeriod], () => {
      loadHistoricalData()
    })
    
    // Lifecycle
    onMounted(() => {
      loadHistoricalData()
    })
    
    return {
      selectedMetric,
      selectedPeriod,
      isLoading,
      showConfigModal,
      isTableExpanded,
      availableMetrics,
      comparisonConfig,
      selectedMetricLabel,
      selectedPeriodLabel,
      visibleMetrics,
      tableViewIcon,
      tableViewLabel,
      changeIndicators,
      trendsChartData,
      trendsChartOptions,
      comparisonTableData,
      loadHistoricalData,
      formatValue,
      formatChange,
      getChangeClass,
      onTrendClick,
      toggleTableView,
      applyConfiguration,
      exportComparison
    }
  }
}
</script>

<style scoped>
.historical-comparison-container {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  overflow: hidden;
}

.comparison-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.header-left .comparison-title {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: inherit;
}

.header-left .comparison-subtitle {
  margin: 0;
  font-size: 12px;
  opacity: 0.7;
}

.header-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}

.comparison-content {
  padding: 20px;
}

.change-indicators {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.change-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.change-indicator:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.indicator-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 18px;
}

.indicator-icon.positive {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.indicator-icon.negative {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.indicator-content {
  flex: 1;
}

.indicator-label {
  display: block;
  font-size: 12px;
  opacity: 0.7;
  margin-bottom: 4px;
}

.indicator-values {
  display: flex;
  align-items: center;
  gap: 8px;
}

.current-value {
  font-size: 16px;
  font-weight: 600;
}

.change-value {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
}

.change-value.positive {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.change-value.negative {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.change-value.neutral {
  background: rgba(156, 163, 175, 0.2);
  color: #9ca3af;
}

.trends-chart-container {
  margin-bottom: 24px;
  height: 300px;
}

.comparison-table-container {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.table-header h6 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.period-cell {
  font-family: monospace;
  font-weight: 600;
}

.metric-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.metric-value {
  font-weight: 600;
}

.metric-change {
  font-size: 11px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 3px;
  margin-top: 2px;
}

.metric-change.positive {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.metric-change.negative {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.metric-change.neutral {
  background: rgba(156, 163, 175, 0.2);
  color: #9ca3af;
}

/* Dark mode styles */
body.dark-mode-active .historical-comparison-container {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .comparison-header {
  background: rgba(255, 255, 255, 0.05);
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .header-left .comparison-title {
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .header-left .comparison-subtitle {
  color: rgba(255, 255, 255, 0.6);
}

body.dark-mode-active .change-indicator {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .change-indicator:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

body.dark-mode-active .comparison-table-container {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .table-header {
  background: rgba(255, 255, 255, 0.05);
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .table-header h6 {
  color: rgba(255, 255, 255, 0.9);
}

/* Responsive */
@media (max-width: 768px) {
  .comparison-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .header-controls {
    flex-direction: column;
    gap: 8px;
  }
  
  .change-indicators {
    grid-template-columns: 1fr;
  }
  
  .metric-cell {
    align-items: flex-start;
  }
}
</style>